import { useEffect, useState } from "react";
import { RefreshCw } from "lucide-react";
import { ResponsiveContainer, ScatterChart, Scatter, XAxis, YAxis, CartesianGrid, Tooltip } from "recharts";

import { useConsumerAuth } from "../context/ConsumerAuthContext";
import consumerApi from "../lib/consumerApi";
import { premiumLiveOddsEnabled } from "./PremiumLiveOdds";

const date = (value) => new Date(value).toLocaleString("en-GB");

export default function PremiumOddsHistory({ competitionId }) {
  const auth = useConsumerAuth();
  if (!premiumLiveOddsEnabled() || !auth?.ready || !auth?.account?.subscription?.entitlement_active) return null;
  return <OwnedHistory key={`${auth.account.user?.user_id}:${competitionId}`} competitionId={competitionId} />;
}

function OwnedHistory({ competitionId }) {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [revision, setRevision] = useState(0);
  const [visible, setVisible] = useState(10);
  useEffect(() => {
    let active = true;
    setData(null); setError(""); setVisible(10);
    consumerApi.get(`/premium/odds-history/${encodeURIComponent(competitionId)}?days=${days}`).then(({ data: result }) => {
      if (active) setData(result);
    }).catch(() => { if (active) setError("History could not be loaded. Missing observations have not been replaced with zero."); });
    return () => { active = false; };
  }, [competitionId, days, revision]);
  const current = (data?.points || []).filter((point) => point.current_entries !== null).map((point) => ({ time: new Date(point.observed_at).getTime(), entries: point.current_entries }));
  return <section className="mt-8 border-y border-white/15 py-5 text-white" data-testid="premium-odds-history">
    <div className="flex flex-wrap items-center justify-between gap-3">
      <h2 className="font-display text-2xl font-semibold">Odds history</h2>
      <label className="text-sm">Period <select aria-label="Odds history period" className="ml-2 rounded border border-white/20 bg-[var(--cc-ink)] p-2" value={days} onChange={(event) => setDays(Number(event.target.value))}>
        <option value={7}>7 days</option>
        <option value={30}>30 days</option>
        <option value={90}>90 days</option>
        <option value={180}>180 days</option>
      </select></label>
    </div>
    {error ? <div className="mt-3 flex items-center gap-3"><p role="alert" className="text-sm">{error}</p><button type="button" aria-label="Retry odds history" title="Retry odds history" className="min-h-10 min-w-10 rounded border border-white/20 p-2" onClick={() => setRevision((value) => value + 1)}><RefreshCw size={18} /></button></div> : !data ? <p role="status" className="mt-3 text-sm">Loading observations...</p> : <>
      {data.message && <p className="mt-3 text-sm text-[var(--cc-text-muted)]" role={data.status === "partial" ? "status" : undefined}>{data.message}</p>}
      {!!current.length && <div className="mt-4 h-52 min-w-0" role="img" aria-label={`Observed current pool, ${current.length} supported observations. Missing periods are unknown.`}>
        <ResponsiveContainer width="100%" height="100%">
          <ScatterChart margin={{ top: 8, right: 8, bottom: 8, left: 8 }}>
            <CartesianGrid stroke="rgba(255,255,255,0.12)" />
            <XAxis dataKey="time" type="number" domain={["dataMin", "dataMax"]} tickFormatter={(value) => new Date(value).toLocaleDateString("en-GB", { day: "numeric", month: "short" })} stroke="#a3a3a3" />
            <YAxis dataKey="entries" type="number" domain={[0, "auto"]} width={56} stroke="#a3a3a3" />
            <Tooltip labelFormatter={date} formatter={(value, name) => name === "time" ? [date(value), "Observed"] : [value, "Current entries"]} />
            <Scatter name="Observed current entries" data={current} fill="#22c55e" />
          </ScatterChart>
        </ResponsiveContainer>
      </div>}
      {!!data.points.length && <div className="mt-4 overflow-x-auto">
        <table className="w-full text-left text-sm"><thead><tr className="border-b border-white/15"><th className="py-2 pr-4">Observed</th><th className="py-2 pr-4">Current odds</th><th className="py-2 pr-4">Maximum pool</th><th className="py-2">Ticket price</th></tr></thead><tbody>
          {[...data.points].reverse().slice(0, visible).map((point) => <tr key={point.observation_id} className="border-b border-white/10"><td className="py-2 pr-4 whitespace-nowrap">{date(point.observed_at)}</td><td className="py-2 pr-4 whitespace-nowrap">{point.current_odds || "Unavailable"}</td><td className="py-2 pr-4">{point.maximum_entries?.toLocaleString("en-GB") ?? "Unknown"}</td><td className="py-2">{point.ticket_price === null ? "Unknown" : `GBP ${point.ticket_price}`}</td></tr>)}
        </tbody></table>
      </div>}
      {data.points.length > visible && <button type="button" className="mt-3 min-h-10 rounded border border-white/20 px-3 text-sm" onClick={() => setVisible((value) => value + 20)}>Older observations</button>}
      <p className="mt-4 text-xs leading-relaxed text-[var(--cc-text-muted)]">{data.disclosure}</p>
    </>}
  </section>;
}
